import {
  DRUG_FILTER_LABELS,
  DRUG_FILTERS,
  type DrugFilter,
} from '@/entities/drug/api/drug.api';
import { usePharmaCompanies } from '@/entities/drug/hooks/useDrugQueries';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/utils';

export interface DrugFilterValue {
  tradable: boolean;
  filters: DrugFilter[];
  pharma: number[];
}

const EMPTY_FILTER: DrugFilterValue = {
  tradable: false,
  filters: [],
  pharma: [],
};

const toggle = <T,>(list: T[], item: T) =>
  list.includes(item) ? list.filter((v) => v !== item) : [...list, item];

/**
 * M-11 필터 레일 — 거래 가능 · 의약품 태그 10종 · 제약사 다중 선택.
 * 데스크톱은 바꾸는 즉시 onChange로 URL을 갱신하고, 모바일 Sheet는 임시 값으로 받아 [필터 적용] 때 반영한다.
 */
export const DrugFilterRail = ({
  keyword,
  value,
  onChange,
  className,
}: {
  /** 제약사 목록 조회 키워드 (현재 검색어) */
  keyword: string;
  value: DrugFilterValue;
  onChange: (next: DrugFilterValue) => void;
  className?: string;
}) => {
  // 선택지는 검색어 · 약품 필터로만 정한다 (선택한 제약사 · 거래 가능 체크로 좁히지 않음)
  const { data: companies, isPending } = usePharmaCompanies(
    keyword,
    value.filters,
  );

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex items-center">
        <h2 className="text-[15px] leading-[23px] font-bold text-ink">필터</h2>
        <button
          type="button"
          onClick={() => onChange(EMPTY_FILTER)}
          className="ml-auto text-xs leading-[18px] text-ink-7 hover:text-ink-4"
        >
          초기화
        </button>
      </div>

      <label className="mt-3.5 flex cursor-pointer items-center gap-[9px] py-[7px]">
        <FilterCheckbox
          checked={value.tradable}
          onChange={(tradable) => onChange({ ...value, tradable })}
        />
        <span
          className={cn(
            'text-xs leading-[18px]',
            value.tradable ? 'font-medium text-ink' : 'text-ink-3',
          )}
        >
          거래 가능한 의약품만
        </span>
      </label>
      <div className="mt-1.5 h-px bg-[#f0f0f0]" />

      <h3 className="mt-3.5 text-xs leading-[18px] font-medium text-ink-6">
        의약품
      </h3>
      <div className="mt-2.5 flex flex-wrap gap-[7px]">
        {DRUG_FILTERS.map((f) => {
          const on = value.filters.includes(f);
          return (
            <button
              key={f}
              type="button"
              aria-pressed={on}
              onClick={() =>
                onChange({ ...value, filters: toggle(value.filters, f) })
              }
              className={cn(
                'rounded-card border px-3 py-[7px] text-[11px] leading-[17px]',
                on
                  ? 'border-ink bg-ink font-medium text-white'
                  : 'border-[#e8e8e8] bg-field text-ink-3 hover:border-line-strong',
              )}
            >
              {DRUG_FILTER_LABELS[f]}
            </button>
          );
        })}
      </div>

      <h3 className="mt-[18px] text-xs leading-[18px] font-medium text-ink-6">
        제약사
      </h3>
      <p className="mt-1 text-[11px] leading-[17px] text-ink-7">
        거래 가능한 제약사만 선택할 수 있어요.
      </p>
      <ul className="mt-2 flex flex-col lg:max-h-[420px] lg:overflow-y-auto">
        {keyword && isPending
          ? Array.from({ length: 5 }, (_, i) => (
              <li key={i} className="py-[7px]">
                <Skeleton className="h-[18px] w-32" />
              </li>
            ))
          : companies?.map((c) => {
              const on = value.pharma.includes(c.pharmaCompanyId);
              return (
                <li key={c.pharmaCompanyId}>
                  <label className="flex cursor-pointer items-center gap-[9px] py-[7px]">
                    <FilterCheckbox
                      checked={on}
                      onChange={() =>
                        onChange({
                          ...value,
                          pharma: toggle(value.pharma, c.pharmaCompanyId),
                        })
                      }
                    />
                    <span
                      className={cn(
                        'text-xs leading-[18px]',
                        on ? 'font-medium text-ink' : 'text-ink-3',
                      )}
                    >
                      {c.pharmaCompanyName}
                    </span>
                  </label>
                </li>
              );
            })}
      </ul>
    </div>
  );
};

/** Figma 필터 체크박스 — 16px · radius 4 · 테두리 #cfcfcf */
const FilterCheckbox = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Checkbox
    checked={checked}
    onCheckedChange={(v) => onChange(v === true)}
    className="size-4 rounded-[4px] border-[#cfcfcf]"
  />
);
